import React, { createContext, useContext, useMemo } from "react";
import { useCoreContext, useCoreStateContext } from "spark-core-dx/contexts";
import { useGenericManager } from "spark-core-dx/hooks";
import { ItemServiceFactory } from "../../services/ItemServiceFactory";

const ResourceDomainContext = createContext(null);

export function ResourceDomainContextProvider({ children }) {
  const coreContext = useCoreContext();
  const coreState = useCoreStateContext();
  const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
  const itemServiceEnum = useMemo(() => itemServiceFactory.ItemServiceEnum, [itemServiceFactory]);

  const resourceConfigurationMgr = useGenericManager(itemServiceEnum.ResourceConfiguration);
  const resourceConfigurationWizardMgr = useGenericManager(itemServiceEnum.ResourceConfigurationWizard);
  const integrationMgr = useGenericManager(itemServiceEnum.Integration);
  const logMgr = useGenericManager(itemServiceEnum.Log);
  const eventMgr = useGenericManager(itemServiceEnum.Event);
  const sourceSystemMgr = useGenericManager(itemServiceEnum.SourceSystem);
  const sourceObjectMgr = useGenericManager(itemServiceEnum.SourceObject);
  const schemaMgr = useGenericManager(itemServiceEnum.Schema);
  const entityObjectMgr = useGenericManager(itemServiceEnum.EntityObject);
  const warehouseMgr = useGenericManager(itemServiceEnum.Warehouse);
  const warehouseViewMgr = useGenericManager(itemServiceEnum.WarehouseView);
  const aiChatSessionMgr = useGenericManager(itemServiceEnum.AIChatSession);
  const aiResourceProvisioningOperationMgr = useGenericManager(itemServiceEnum.AIResourceProvisioningOperation);
  const fileStorageMgr = useGenericManager(itemServiceEnum.FileStorage);
  const externalSystemMgr = useGenericManager(itemServiceEnum.ExternalSystem);
  const schemaAPIMgr = useGenericManager(itemServiceEnum.SchemaAPI);
  const destinationScheduleMgr = useGenericManager(itemServiceEnum.DestinationSchedule);
  const productMarketplaceCatalogMgr = useGenericManager(itemServiceEnum.ProductMarketplaceCatalog);
  const clientMgr = useGenericManager(itemServiceEnum.Client || "Client");
  const profileMgr = useGenericManager(itemServiceEnum.Profile || "Profile");
  const clientResourceMgr = useGenericManager(itemServiceEnum.ClientResource);
  const resourceUsageEstimateMgr = useGenericManager(itemServiceEnum.ResourceUsageEstimate);
  const resourceConfigurationReviewSummaryMgr = useGenericManager(itemServiceEnum.ResourceConfigurationReviewSummary);
  const ediTradingPartnerMgr = useGenericManager(itemServiceEnum.EdiTradingPartner);
  const ediTradingPartnerGroupMgr = useGenericManager(itemServiceEnum.EdiTradingPartnerGroup);
  const ediPartnerScopePolicyMgr = useGenericManager(itemServiceEnum.EdiPartnerScopePolicy);

  const value = useMemo(() => ({
    coreContext,
    coreState,
    itemServiceFactory,
    itemServiceEnum,
    managers: {
      resourceConfiguration: resourceConfigurationMgr,
      resourceConfigurationWizard: resourceConfigurationWizardMgr,
      integration: integrationMgr,
      log: logMgr,
      event: eventMgr,
      sourceSystem: sourceSystemMgr,
      sourceObject: sourceObjectMgr,
      schema: schemaMgr,
      entityObject: entityObjectMgr,
      warehouse: warehouseMgr,
      warehouseView: warehouseViewMgr,
      aiChatSession: aiChatSessionMgr,
      aiResourceProvisioningOperation: aiResourceProvisioningOperationMgr,
      fileStorage: fileStorageMgr,
      externalSystem: externalSystemMgr,
      schemaAPI: schemaAPIMgr,
      destinationSchedule: destinationScheduleMgr,
      productMarketplaceCatalog: productMarketplaceCatalogMgr,
      client: clientMgr,
      profile: profileMgr,
      clientResource: clientResourceMgr,
      resourceUsageEstimate: resourceUsageEstimateMgr,
      resourceConfigurationReviewSummary: resourceConfigurationReviewSummaryMgr,
      ediTradingPartner: ediTradingPartnerMgr,
      ediTradingPartnerGroup: ediTradingPartnerGroupMgr,
      ediPartnerScopePolicy: ediPartnerScopePolicyMgr
    }
  }), [
    aiChatSessionMgr,
    aiResourceProvisioningOperationMgr,
    clientMgr,
    clientResourceMgr,
    coreContext,
    coreState,
    destinationScheduleMgr,
    ediPartnerScopePolicyMgr,
    ediTradingPartnerGroupMgr,
    ediTradingPartnerMgr,
    eventMgr,
    entityObjectMgr,
    externalSystemMgr,
    fileStorageMgr,
    integrationMgr,
    itemServiceEnum,
    itemServiceFactory,
    logMgr,
    profileMgr,
    productMarketplaceCatalogMgr,
    resourceConfigurationMgr,
    resourceConfigurationReviewSummaryMgr,
    resourceConfigurationWizardMgr,
    resourceUsageEstimateMgr,
    schemaAPIMgr,
    schemaMgr,
    sourceObjectMgr,
    sourceSystemMgr,
    warehouseMgr,
    warehouseViewMgr
  ]);

  return (
    <ResourceDomainContext.Provider value={value}>
      {children}
    </ResourceDomainContext.Provider>
  );
}

export const useResourceDomainContext = () => useContext(ResourceDomainContext);
